<template>
	<li>
		<template v-if="showItemLabel">
			<a v-if="clickable" class="label" href="#" @click.prevent="$emit('edit', item)">
				<LabelContent
					:item="item"
					:label-type="labelType"
					:is-last-level="isLastLevel"
					:has-selected-childs-to-last-level="hasSelectedChildsToLastLevel"
					:has-icon="hasIcon"
					:icon-path="iconPath"
					:level="level"
					:no-children-appendix="noChildrenAppendix"
					:label-appendix="labelAppendix"
				/>
			</a>
			<span v-else class="label">
				<LabelContent
					:item="item"
					:label-type="labelType"
					:is-last-level="isLastLevel"
					:has-selected-childs-to-last-level="hasSelectedChildsToLastLevel"
					:has-icon="hasIcon"
					:icon-path="iconPath"
					:level="level"
					:no-children-appendix="noChildrenAppendix"
					:label-appendix="labelAppendix"
				/>
			</span>
		</template>
		<Additions
			v-if="showAdditions && hasAdditions"
			:is-list-styling="isListStyling"
			:list="list"
			:experience="experience"
			:self-assessment="selfAssessment"
			:supplements="supplements"
			:with-edit-button="withEditButton"
			:deletable="deletable"
			:specificities="specificities"
			@edit="$emit('edit', item)"
			@delete="$emit('delete', item)"
		/>

		<ul v-if="childs.length > 0" :class="['characteristic-selected-items', 'level-' + (level + 1)]">
			<characteristic-selected-items-item
				v-for="item in childs"
				:key="item.item.id"
				:item="item"
				:selected="selected"
				:show-experience="showExperience"
				:show-additions="showAdditions"
				:specificities-provider="specificitiesProvider"
				:deletable="deletable"
				:with-edit-button="withEditButton"
				:level="level + 1"
				:no-children-appendix="noChildrenAppendix"
				:label-appendix-provider="labelAppendixProvider"
				:label-type="labelType"
				:clickable="clickable"
				@edit="$emit('edit', $event)"
				@delete="$emit('delete', $event)"
			/>
		</ul>
	</li>
</template>

<script>
import Label from "@/vue/characteristic/Label";
import Icon from "@/vue/icon/Icon";
import _ from "lodash";
import {isDefined} from "@/helper/Value";
import {getLabel} from "@/helper/characteristic/Requirement";
import {isEmptyOrNull} from "@/helper/String";
import {arrayIsNotEmpty} from "@/helper/Array";
import {hasSelectedChildsToLastLevel} from "@/helper/characteristic/Characteristic";
import * as CharacteristicLabel from "@/helper/characteristic/Label"
import {STYLING__LIST} from "@/vue/characteristic/SelectedItems"
import LabelContent from "@/vue/characteristic/selected-items/LabelContent.vue";
import Additions from "@/vue/characteristic/selected-items/Additions.vue";
import {sort} from "@/helper/characteristic/Sort";

export default {

	components: {
		Additions,
		LabelContent,
		Icon,
		Label,
	},

	props: {
		item: {
			type: Object,
			required: true,
		},
		selected: {
			type: Array,
			required: true,
		},
		showExperience: {
			type: Boolean,
			required: false,
			default: true,
		},
		showAdditions: {
			type: Boolean,
			required: false,
			default: true,
		},
		showItemLabel: {
			type: Boolean,
			required: false,
			default: true,
		},
		withEditButton: {
			type: Boolean,
			required: false,
			default: false,
		},
		iconPath: {
			type: String,
			required: false,
		},
		specificitiesProvider: {
			type: Function,
			required: false,
		},
		labelAppendixProvider: {
			type: Function,
			required: false,
		},
		deletable: {
			type: Boolean,
			required: false,
			default: false,
		},
		clickable: {
			type: Boolean,
			required: false,
			default: false,
		},
		level: {
			type: Number,
			required: false,
			default: 1,
		},
		noChildrenAppendix: {
			type: [String, Object],
			required: false,
		},
		labelType: {
			type: String,
			required: false,
		},
		styling: {
			type: String,
			required: false,
		}
	},

	computed: {
		labelAppendix ()
		{
			if (!this.labelAppendixProvider)
			{
				return undefined;
			}

			return this.labelAppendixProvider({
				item: this.item
			});
		},
		isListStyling ()
		{
			return this.styling === STYLING__LIST;
		},
		hasSelectedChildsToLastLevel ()
		{
			// free text
			if (!this.item.item)
			{
				return false;
			}

			return hasSelectedChildsToLastLevel(this.selected, this.item.item);
		},
		hasAdditions ()
		{
			return !isEmptyOrNull(this.experience)
				|| !isEmptyOrNull(this.selfAssessment)
				|| !isEmptyOrNull(this.supplements)
				|| !isEmptyOrNull(this.requirement)
				|| isDefined(this.specificities);
		},
		experience ()
		{
			if (!this.showExperience || this.item.experience === undefined)
			{
				return null;
			}

			return this.item.experience === null
				? 'Keine Erfahrung'
				: CharacteristicLabel.get(this.item.experience, this.labelType);
		},
		selfAssessment ()
		{
			return isDefined(this.item.selfAssessment)
				? CharacteristicLabel.get(this.item.selfAssessment, this.labelType)
				: null;
		},
		supplements ()
		{
			let parts = [];

			if (this.item.supplements)
			{
				_.forEach(this.item.supplements, supplementItem =>
				{
					_.forEach(supplementItem.items, item =>
					{
						parts.push(
							CharacteristicLabel.get(item.item, this.labelType)
						);
					});
				});
			}

			return parts.length > 0
				? _.join(parts, ', ')
				: null;
		},
		specificities ()
		{
			if (this.specificitiesProvider)
			{
				return this.specificitiesProvider({
					item: this.item
				});
			}

			let specificities = [];

			if (this.item.specificities && arrayIsNotEmpty(this.item.specificities))
			{
				_.forEach(this.item.specificities, allocation =>
				{
					specificities.push({
						label: allocation.specificityType.label,
						value: allocation.specificity.label
					});
				});
			}

			return specificities.length > 0 ? specificities : null;
		},
		list ()
		{
			const list = [];

			if (this.experience)
			{
				list.push(this.experience);
			}

			if (this.selfAssessment)
			{
				list.push(this.selfAssessment);
			}

			if (this.supplements)
			{
				list.push(this.supplements);
			}

			return list.join(' | ');
		},
		requirement ()
		{
			return this.item.requirement
				? getLabel(this.item.requirement.id)
				: null;
		},
		isLastLevel ()
		{
			if (!this.item.item) // free text
			{
				return true;
			}

			return this.item.item.children.length === 0;
		},
		childs ()
		{
			if (!this.item.item) // can be for freeText items (software special)
			{
				return [];
			}

			const childIds = _.map(this.item.item.children, 'id');

			const filtered = _.filter(this.selected, item => _.includes(childIds, item.item?.id));

			return sort(filtered);
		},
		hasIcon ()
		{
			return isDefined(this.iconPath)
				&& !isEmptyOrNull(this.iconPath)
				&& isDefined(this.item.item.naturalId)
				&& !isEmptyOrNull(this.item.item.naturalId);
		},
	},
}
</script>
